/**
 * Servico que lida com o processameto de dados
 * para o relatorio, isto eh, pega o payload bruto
 * e adapta o mesmo para a visualizacao devida
 * no documento final
 */
import getSimpleChartEchartOption from "../../components/Charts/Simple/echarts/echartOptionResolver";
import {
  BAR,
  BARRAS_AGRUPADAS,
  FUNNEL,
  HORIZONTAL_STACKED,
  LINE,
} from "../../components/Charts/types";
import { formatEvolutiveData } from "../../sections/GroupCards/EvolutiveChartCard/formatData";
import { formatSimpleChartData } from "../../sections/GroupCards/SimpleChartCard/formatData";
import { formatSimpleCrossData } from "../../sections/GroupCards/SimpleCrossChartCard/formatData";
import getTheme from "./reportThemeService";

import { GRADE_MULTIPLA, GRADE_UNICA } from "src/models/questionTypes";
import { getQuillContentsText } from "../../components/RichTextBox/Quill/quillUtils";
import { getReportEvolutiveChartEchartOption } from "./chart/ReportEvolutiveChartEchartOptionFactory";
import { calculateReportChartColors } from "./chart/calculateReportChartColor";
import { typedefToChartType } from "./chart/colorProcessor";
import { ReportEchartOptionPreProcessor } from "./chart/echartOptionPreProcessors";
import { formatToDecimalPlacesReturningNumber } from "src/util/formatters";

const MAX_GRADE_CHART_ALTERNATIVE_LENGTH = 32;

async function processReportData(reportData, authToken, reportColorConfig) {
  const { questions } = reportData;
  /**
   * Por questoes de performance, todo o mapeamento
   * ocorre na mesma funcao, compartilhando o contexto
   */
  const questionIdToSimpleCrossMap = {};
  const questionIdToChartsMap = {};

  const activeTheme = window.localStorage.getItem("themeActive");

  const theme = getTheme(activeTheme);

  /**
   * Estrategias de processamento dos dados de plotagem,
   * os resultados sao anexados nos mapas criados anteriormente
   */
  const processorStrategies = {
    CRUZAMENTO_SIMPLES: (perguntaRelatorio, dadoPlotagem) => {
      const dadoPlotagemFormatadoData = formatSimpleCrossData(
        dadoPlotagem.data,
        100
      );

      /**
       * Quando typescript for integrado ao sistema, o contrato de resultados de cruzamento simples
       * deve ser devidamente mapeado, assim como o contrato esperado para o relatório, para não
       * gerar esse tipo de solução alternativa.
       */
      if (typeof dadoPlotagemFormatadoData.configuracao !== "undefined") {
        delete dadoPlotagemFormatadoData.configuracao;
      }

      questionIdToSimpleCrossMap[perguntaRelatorio.id] = {
        notes: getQuillContentsText(dadoPlotagem.anotacoes),
        data: dadoPlotagemFormatadoData,
      };
    },

    GERAIS: (perguntaRelatorio, dadoPlotagem, colorsByQuestion) => {
      let chartType = dadoPlotagem.renderType || BAR;

      if (!questionIdToChartsMap[perguntaRelatorio.id]) {
        questionIdToChartsMap[perguntaRelatorio.id] = [];
      }

      const perguntasFormatadas = dadoPlotagem.data.perguntas.map(
        (pergunta) => ({
          ...pergunta,
          alternativas: formatSimpleChartData(pergunta.alternativas, 100)
            /**
             * TEN-1256: Garantir que os resultados percentuais para gráficos simples possuam apenas uma
             * casa decimal no relatório
             */
            .map((resultados) => ({
              ...resultados,
              resultado_percentual: formatToDecimalPlacesReturningNumber(resultados.resultado_percentual, 1)
            })),
        })
      );
      const dadoPlotagemFormatadoData = {
        ...dadoPlotagem.data,
        perguntas: perguntasFormatadas,
      };

      const alternativas = dadoPlotagemFormatadoData.perguntas[0].alternativas;
      const isGrade = [GRADE_UNICA, GRADE_MULTIPLA].includes(
        dadoPlotagemFormatadoData.perguntas[0].tipo
      );
      
      /**
       * Por padrão os dados estão vindo em ASC, então se o usuário quiser os dados em ASC
       * mustReverse deve ser true, ja que no toEchartOption isso significa que irá ocorrer um
       * reverse()
       */
      const mustReverse = dadoPlotagem.renderOrder === "ASC";
      
      /**
       * Para manter o relatório gerando a tabela para tipo grade corretamente,
       * apenas exportamos um gráfico customizado para pergunta do tipo grade
       * se este gráfico possuir menos de dez alternativas, pois acima disso
       * o docx4j entende que uma tabela deve ser gerada, que só suporta o
       * echartOption gerado para gráfico simples.
      */
      if (isGrade && alternativas.length < MAX_GRADE_CHART_ALTERNATIVE_LENGTH) {
        chartType = HORIZONTAL_STACKED;
      }
      
      const echartOptions = ReportEchartOptionPreProcessor.process(
        chartType,
        getSimpleChartEchartOption(chartType, alternativas, theme.colors.charts, mustReverse)
      );
      echartOptions.series = echartOptions.series.map((serie) => {
        const data = serie.data.filter((dataEntry) => dataEntry);
        return {
          ...serie,
          data,
        };
      });

      if (colorsByQuestion) {
        /**
         * Gráfico de grade não possui a propriedade "visibleLegends", apenas "legend"
         */
        const legends = !isGrade ? echartOptions.visibleLegends : echartOptions.legend.data;

        const calculatedColors = calculateReportChartColors({
          data: legends,
          theme: theme,
          chartsColors: colorsByQuestion,
        });

        if (Array.isArray(calculatedColors) && calculatedColors.length) {
          echartOptions.color = calculatedColors;
        }
      }

      const notes = getQuillContentsText(dadoPlotagem.anotacoes);

      questionIdToChartsMap[perguntaRelatorio.id].push({
        feature: "simple",
        type: chartType,
        notes,
        renderOptions: {
          option: echartOptions,
        },
      });
    },

    /**
     * perguntaRelatorio = {id_configuracao, titulo_configuracao, resultado}
     */
    EVOLUTIVO: (
      perguntaRelatorio,
      dadosPlotagem,
      colorsByQuestion,
      correspondingResultadosGeraisData
    ) => {
      let chartType = dadosPlotagem.renderType || LINE;

      if (!questionIdToChartsMap[perguntaRelatorio.id]) {
        questionIdToChartsMap[perguntaRelatorio.id] = [];
      }

      const formattedData = formatEvolutiveData(dadosPlotagem.data.resultado);

      const resolvedChartType = chartType === BARRAS_AGRUPADAS ? BAR : chartType;

      const echartOptions = ReportEchartOptionPreProcessor.process(
        resolvedChartType,
        getReportEvolutiveChartEchartOption(
          resolvedChartType,
          formattedData,
          theme.colors.charts
        )
      );

      if (colorsByQuestion) {
        const calculatedColors = calculateReportChartColors({
          data: echartOptions.visibleLegends,
          theme: theme,
          chartsColors: colorsByQuestion,
          simpleChartInheritanceConfig: correspondingResultadosGeraisData,
        });

        if (Array.isArray(calculatedColors) && calculatedColors.length) {
          echartOptions.color = calculatedColors;
        }
      }

      const evolutive = {
        title: dadosPlotagem.data.titulo_configuracao,
        feature: "evolutive",
        type: chartType,
        notes: dadosPlotagem.anotacoes,
        renderOptions: {
          option: echartOptions,
        },
      };

      questionIdToChartsMap[perguntaRelatorio.id].push(evolutive);
    },
  };

  /**
   * Recupera as configurações de cores atuais e legendas de todos os gráficos
   * simples para cada pergunta da pesquisa e cliente atualmente selecionados
   * O objetivo aqui é permitir que cores herdadas do gráfico simples para o
   * gráfico evolutivo sejam persistidas no relatório
   */
  const perguntasSimplesDadosHeranca = questions.map((perguntaRelatorio) => {
    const dadoPlotagemSimples = perguntaRelatorio.dados_plotagem_relatorio.find(
      (dadoPlotagem) => {
        return dadoPlotagem.resultado_type_def === "GERAIS";
      }
    );

    const colorsByQuestion = reportColorConfig.find(
      (colorConfig) =>
        colorConfig.questionId === perguntaRelatorio.id &&
        colorConfig.type === typedefToChartType.GERAIS
    );

    const colors = { ...((colorsByQuestion && colorsByQuestion.colors) || {}) };

    /**
     * O gráfico simples pode ter sido desmarcado no gerenciador de relatório, e
     * nesse caso o plot GERAIS não vem no payload. Sem gráfico simples não existe
     * legenda de onde herdar cor, então o evolutivo cai no tema, que é o esperado.
     */
    if (!dadoPlotagemSimples) {
      return {
        questionId: perguntaRelatorio.id,
        colors,
        legendas: [],
      };
    }

    const perguntasFormatadas = dadoPlotagemSimples.data.perguntas.map(
      (pergunta) => ({
        ...pergunta,
        alternativas: formatSimpleChartData(pergunta.alternativas, 100),
      })
    );

    const echartOptions = getSimpleChartEchartOption(
      BAR,
      perguntasFormatadas[0].alternativas,
      theme.colors.charts
    );

    return {
      questionId: perguntaRelatorio.id,
      colors,
      legendas: [...(echartOptions.visibleLegends || [])],
    };
  });

  /**
   * Mapeia as perguntas para seus titulos e dispara o
   * processamento dos dados de plotagem
   */
  questions.forEach((perguntaRelatorio) => {
    const { id: questionId } = perguntaRelatorio;

    const correspondingResultadosGeraisData = perguntasSimplesDadosHeranca.find(
      (dadosHeranca) => {
        return dadosHeranca.questionId === questionId;
      }
    );

    perguntaRelatorio.dados_plotagem_relatorio.forEach((dadoPlotagem) => {
      const colorsByQuestion = reportColorConfig.find((colorConfig) => {
        return (
          colorConfig.questionId === questionId &&
          typedefToChartType[dadoPlotagem.resultado_type_def] ===
            colorConfig.type
        );
      });

      const rawColors = colorsByQuestion && colorsByQuestion.colors;

      processorStrategies[dadoPlotagem.resultado_type_def](
        perguntaRelatorio,
        dadoPlotagem,
        rawColors,
        correspondingResultadosGeraisData
      );
    });
  });

  /**
   * Dos dados necessarios para plotagem final das perguntas
   */
  const questionData = questions.map((perguntaRelatorio) => ({
    id: perguntaRelatorio.id,
    title: perguntaRelatorio.enunciado,
    simpleCross: questionIdToSimpleCrossMap[perguntaRelatorio.id],
    /**
     * Quando a pergunta só exporta cruzamento simples, nenhum gráfico é montado.
     * O contrato do relatório espera a lista sempre presente: omitir o atributo
     * faz o gerador estourar NullPointerException ao percorrer os gráficos.
     */
    charts: questionIdToChartsMap[perguntaRelatorio.id] || [],
  }));

  /**
   * Definicao final do payload
   */
  const finalPayload = {
    profile: process.env.NODE_ENV,
    bearerToken: authToken,
    surveyTitle: reportData.surveyTitle,
    survey: {
      ...reportData.survey,
    },
    client_name: reportData.client_name,
    questions: questionData,
    surveyId: reportData.surveyId,
    customization: reportData.customization,
  };

  return finalPayload;
}

export { processReportData };
